import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
    ShieldCheck,
    Mail,
    ArrowLeft,
    CheckCircle2,
    Send,
} from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-5 login-scope">
            {/* Left hero — reused visual language */}
            <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden text-white p-10 xl:p-14 flex-col login-hero">
                <div className="login-hero__grid" aria-hidden />
                <div className="login-hero__glow" aria-hidden />
                <div className="relative z-10 max-w-2xl mt-auto mb-auto">
                    <div className="login-brandmark" style={{ width: 60, height: 60, borderRadius: 16 }}>
                        <ShieldCheck size={28} strokeWidth={2} />
                    </div>
                    <h1 className="login-hero__title mt-6">
                        Locked out?
                        <br />
                        We&rsquo;ll get you back in.
                    </h1>
                    <p className="login-hero__tagline">
                        A one-time link, valid for 60 minutes. Never a phone call, never an OTP over email.
                    </p>
                    <div className="login-hero__accent-line" />
                    <div className="mt-8 max-w-md text-[13px] text-slate-300 leading-relaxed">
                        For your safety: we&rsquo;ll always confirm the request to the email on file, and
                        the link works exactly once. If nothing arrives in 2 minutes, check your spam.
                    </div>
                </div>
            </div>

            {/* Right card */}
            <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-stone-50">
                <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] p-8 sm:p-10 login-card" data-testid="forgot-form">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="login-brandmark">
                            <ShieldCheck size={22} strokeWidth={2} />
                        </div>
                        <div className="leading-tight">
                            <div className="text-[15px] font-bold text-slate-900 tracking-[0.14em] leading-none">
                                CREDIT CARD
                            </div>
                            <div className="text-[13px] font-semibold text-slate-500 tracking-[0.14em] leading-none mt-1.5">
                                CONTROL CENTER
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <h2 className="text-[26px] font-semibold text-slate-900 leading-tight">
                            Forgot your password?
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Enter your email — we&rsquo;ll send a reset link.
                        </p>
                    </div>

                    {!sent ? (
                        <form onSubmit={onSubmit}>
                            <label className="block mb-4">
                                <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
                                    Email Address
                                </span>
                                <span className="login-field">
                                    <Mail size={16} className="login-field__icon" strokeWidth={1.9} />
                                    <input
                                        type="email"
                                        autoComplete="username"
                                        data-testid="forgot-email-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        className="login-field__input"
                                    />
                                </span>
                            </label>

                            {error && (
                                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="forgot-error">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={busy}
                                className="login-signin-btn"
                                data-testid="forgot-submit-btn"
                            >
                                <Send size={16} strokeWidth={2.1} />
                                {busy ? "Sending…" : "Send reset link"}
                            </button>
                        </form>
                    ) : (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="forgot-sent">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 size={22} className="text-emerald-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-emerald-900 font-semibold">Check your inbox.</div>
                                    <div className="text-emerald-800 text-sm mt-1 leading-relaxed">
                                        If <span className="font-medium">{email}</span> is registered,
                                        a reset link is on its way. The link expires in 60 minutes
                                        and can be used once.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 pt-5 border-t border-slate-100">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            data-testid="forgot-back-link"
                        >
                            <ArrowLeft size={14} /> Back to sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
